"use client";

import { useEffect, useRef } from "react";
import type { ThumbMovementDriveState } from "../components/ThumbMovementControls";
import {
	normalizeThumbLocoKey,
	resolveThumbDriveStateFromArrowKeys,
} from "../lib/thumb-movement-direction";

interface UseHexagonMapThumbKeyboardOptions {
	enabled?: boolean;
	onDriveChange: (state: ThumbMovementDriveState | null) => void;
	onDriveEnd: () => void;
	onKeyboardDrivingChange?: (isDriving: boolean) => void;
}

function shouldHandleThumbKeyboardEvent(event: KeyboardEvent): boolean {
	const target = event.target;
	if (!(target instanceof HTMLElement)) {
		return true;
	}
	if (target.isContentEditable) {
		return false;
	}
	const tag = target.tagName;
	return tag !== "INPUT" && tag !== "TEXTAREA" && tag !== "SELECT";
}

/**
 * Window-level arrow + WASD locomotion when the map surface is active
 * (same key set as scene free loco). Skips editable fields.
 */
export function useHexagonMapThumbKeyboard(
	options: UseHexagonMapThumbKeyboardOptions,
) {
	const {
		enabled = true,
		onDriveChange,
		onDriveEnd,
		onKeyboardDrivingChange,
	} = options;

	const pressedKeysRef = useRef(new Set<string>());
	const onDriveChangeRef = useRef(onDriveChange);
	const onDriveEndRef = useRef(onDriveEnd);
	const onKeyboardDrivingChangeRef = useRef(onKeyboardDrivingChange);
	onDriveChangeRef.current = onDriveChange;
	onDriveEndRef.current = onDriveEnd;
	onKeyboardDrivingChangeRef.current = onKeyboardDrivingChange;

	useEffect(() => {
		if (!enabled) {
			pressedKeysRef.current.clear();
			return;
		}

		const emitDriveFromKeys = () => {
			const driveState = resolveThumbDriveStateFromArrowKeys(
				pressedKeysRef.current,
			);
			onKeyboardDrivingChangeRef.current?.(driveState != null);
			if (driveState) {
				onDriveChangeRef.current(driveState);
				return;
			}
			onDriveEndRef.current();
		};

		const onKeyDown = (event: KeyboardEvent) => {
			const locoKey = normalizeThumbLocoKey(event.key);
			if (locoKey == null || !shouldHandleThumbKeyboardEvent(event)) {
				return;
			}
			event.preventDefault();
			if (pressedKeysRef.current.has(locoKey)) {
				return;
			}
			pressedKeysRef.current.add(locoKey);
			emitDriveFromKeys();
		};

		const onKeyUp = (event: KeyboardEvent) => {
			const locoKey = normalizeThumbLocoKey(event.key);
			if (locoKey == null || !shouldHandleThumbKeyboardEvent(event)) {
				return;
			}
			event.preventDefault();
			if (!pressedKeysRef.current.delete(locoKey)) {
				return;
			}
			emitDriveFromKeys();
		};

		const clearKeys = (notifyEnd: boolean) => {
			const hadKeys = pressedKeysRef.current.size > 0;
			pressedKeysRef.current.clear();
			if (!hadKeys) {
				return;
			}
			onKeyboardDrivingChangeRef.current?.(false);
			if (notifyEnd) {
				onDriveEndRef.current();
			}
		};

		const onWindowBlur = () => {
			clearKeys(true);
		};

		window.addEventListener("keydown", onKeyDown);
		window.addEventListener("keyup", onKeyUp);
		window.addEventListener("blur", onWindowBlur);

		return () => {
			window.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("keyup", onKeyUp);
			window.removeEventListener("blur", onWindowBlur);
			clearKeys(false);
		};
	}, [enabled]);
}
